import jwt, { decode } from "jsonwebtoken";
import jwkToPem from "jwk-to-pem";
import querystring from "querystring";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { usePkceGen } from "../hooks/usePkceGen";
import { Common, _log } from "../lib/Common";
import { LocalStorageHelper } from "../lib/LocalStorageHelper";
import { AkamaiApiHelper, SelfServiceApiHelper } from "./RequestHelper";

export const AkamaiApi = (isBusiness) => {
    const { generatePkceChallenge } = usePkceGen();
    const [pckeSticky, setPckeSticky] = useLocalStorage({}, "pckeStickyVal");
    const [returnUrlSticky, setReturnUrlSticky] = useLocalStorage("", "returnUrlSticky");

    isBusiness = isBusiness === true;

    const clientId = isBusiness
        ? process.env.REACT_APP_AKAMAI_BUSINESS_OIDC_CLIENT_ID
        : process.env.REACT_APP_AKAMAI_OIDC_CLIENT_ID;

    const redirectUri = Common.parseUrl(
        isBusiness
            ? process.env.REACT_APP_AKAMAI_BUSINESS_AUTH_RETURN_URL
            : process.env.REACT_APP_AKAMAI_AUTH_RETURN_URL
    );

    function akamaiUrl(path) {
        return process.env.REACT_APP_AKAMAI_HOSTED_URL + path;
    }

    function handleError(errorMsg, onError) {
        _log("Akamai Error", errorMsg);

        if (onError) {
            onError(errorMsg);
        }
    }

    function akamaiFormPost(functionName, path, data, callback, onError) {
        var url = akamaiUrl(path);

        let _callBack = (response) => {
            _log(functionName + " response: ", response);
            if (callback) {
                callback(response);
            }
        };

        _log(`FormPost ${functionName}, ${url}`);

        new AkamaiApiHelper()
            .withPath(path)
            .usingCallback(_callBack)
            .usingErrorHandling((errorMsg) => handleError(errorMsg, onError))
            .formPost(querystring.stringify(data), true);
    }

    function buildRegistrationUrl(returnUrl, callback) {
        buildAkamaiAuthorizeUrl(returnUrl, null, null, callback, "create");
    }

    function buildAkamaiAuthorizeUrl(returnUrl = null, data = null, email = null, callback, prompt = "login") {
        generatePkceChallenge((verifier, challenge) => {
            returnUrl = returnUrl?.includes("#//")
                ? returnUrl?.replace("#//", "#/")
                : returnUrl;

            let state = makeId(40);
            setPckeSticky({ verifier, challenge, state });
            setReturnUrlSticky(returnUrl);

            let claim = {
                "sf_customer_id": null,
                "country_code": null
            };

            //Note: can only pass in specific values as they will get removed by Akamai
            //is_wakanda is valid
            if (data != null) {
                claim[data] = null;
            }

            let claims = {
                "userinfo": claim,
                "id_token": claim
            };
            claims = JSON.stringify(claims);

            let baseUrl = `${process.env.REACT_APP_AKAMAI_HOSTED_URL}/login/authorize`;

            var params = [];

            function appendParam(name, value) {
                var encodedValue = encodeURIComponent(value);
                params.push(`${name}=${encodedValue}`);
            }

            appendParam("client_id", clientId);
            appendParam("redirect_uri", redirectUri);
            appendParam("scope", "openid email profile");
            appendParam("claims", claims);
            appendParam("code_challenge", challenge);
            appendParam("code_challenge_method", "S256");
            appendParam("response_type", "code");
            appendParam("state", state);
            appendParam("prompt", prompt);

            if (email) {
                appendParam("login_hint", email)
            }

            let query = params.join("&");
            let url = `${baseUrl}?${query}`;

            LocalStorageHelper.log(url);

            if (callback) {
                callback(url);
            }
        });
    }

    // function buildProfileUrl(returnUrl = null, data = null, callback = null) {
    //     let baseUrl = `${process.env.REACT_APP_AKAMAI_HOSTED_URL}/auth-ui/profile`;

    //     let url = `${baseUrl}?client_id=${clientId}&redirect_uri=${redirectUri}`;

    //     setReturnUrlSticky(returnUrl);

    //     if (callback) {
    //         callback(url);
    //     }
    // }

    function validateToken(response, callback, error) {
        _log("validateToken");
        let jwtToken = decode(response.data.id_token, { complete: true });

        verifyIdToken(response.data.access_token, jwtToken, response.data.id_token, (result) => {
            if (result.valid) {
                if (callback) {
                    callback(response.data, result.decodedIdToken);
                }
            } else {
                let msg = `Id token is invalid. Please check with administrator - Details - ${result.message}`;
                _log(msg);

                if (error) {
                    error(msg);
                }
            }
        });
    }

    function getAkamaiAccessAndRefreshTokens(code, csrf_state, callback, onError) {
        console.log('Errmmm')
        if (csrf_state !== pckeSticky.state) {
            if (onError) {
                onError(`CSRF state incorrect, Received:${csrf_state} and Saved:${pckeSticky.state}`);
                return;
            }
        }

        let params = {
            client_id: clientId,
            redirect_uri: redirectUri,
            grant_type: "authorization_code",
            code: code,
            code_verifier: pckeSticky.verifier
        }

        _log("getAkamaiAccessAndRefreshTokens");
        _log("params", params);

        let handleResponseCallback = (response) => {
            validateToken(response, callback, onError);
        }

        _log("Going to do akamaiFormPost");
        akamaiFormPost("getAkamaiAccessAndRefreshTokens", "/login/token", params, handleResponseCallback, onError);
    }

    //https://identitydocs.akamai.com/home/customer-idlogintoken
    function refreshAkamaiToken(refreshToken, callback, onError) {
        let params = {
            client_id: clientId,
            redirect_uri: redirectUri,
            grant_type: "refresh_token",
            refresh_token: refreshToken
        }

        let handleResponseCallback = (response) => {
            validateToken(response, callback, onError);
        }

        akamaiFormPost("refreshAkamaiToken", "/login/token", params, handleResponseCallback, onError);
    }

    function verifyIdToken(accessToken, jwtToken, jwtTokenStr, callback = null) {
        _log("VerifyIdToken: jwt header - ", jwtToken.header);

        getAkamaiJWKPublicToken(jwtToken.header.kid, jwtToken.header.alg,
            (publicKey) => {
                if (!publicKey) {
                    return;
                }

                jwt.verify(jwtTokenStr, publicKey,
                    (err, decoded) => {
                        _log("jwt.verify:jwtToken,err,decoded-", jwtToken, err, decoded);

                        if (err) {
                            akamaiTokenRevoke(accessToken,
                                () => {
                                    if (callback) {
                                        callback({ valid: false, message: "Invalid Id token. Token revoke successful, " + err, decodedIdToken: {} });
                                    }
                                }
                            )
                        }
                        else if (callback) {
                            callback({ valid: true, message: "Id token valid", decodedIdToken: decoded });
                        }
                    }
                );
            },
            (errorMsg) => {
                akamaiTokenRevoke(accessToken,
                    () => {
                        if (callback) {
                            callback({ valid: false, message: errorMsg, decodedIdToken: {} });
                        }
                    }
                )
            }
        )
    }

    function getAkamaiJWKPublicToken(kid, alg, callback = null, onError = null) {
        let handleResponse = (response) => {
            _log("getAkamaiJWKPublicToken response: ", response);

            let keys = response.data.keys;
            let jwk = keys.find(o => o.kid === kid && o.alg === alg);

            let pem = jwkToPem(jwk);

            _log("getAkamaiJWKPublicToken - keys, jwk, pem:", keys, jwk, pem);

            if (callback) {
                callback(pem);
            }
        };

        LocalStorageHelper.setIsBusinessUser(isBusiness);

        //Handled by DSTV service app due to CORS
        new SelfServiceApiHelper()
            .withPath("/akamai/login/jwk")
            .usingCallback(handleResponse)
            .usingErrorHandling((errorMsg) => handleError(errorMsg, onError))
            .get();
    }

    function akamaiTokenRevoke(access_token, callback, onError) {
        let params = {
            client_id: clientId,
            token: access_token
        }

        _log("akamaiTokenRevoke params,", params)

        akamaiFormPost("akamaiTokenRevoke", "/login/token/revoke", params, callback, onError);
    }

    function makeId(length) {
        let result = "";
        let characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    const getReturnUrl = () => returnUrlSticky;

    const resetReturnUrl = () => setReturnUrlSticky("");

    return {
        buildAkamaiAuthorizeUrl,
        buildRegistrationUrl,
        getAkamaiAccessAndRefreshTokens,
        akamaiTokenRevoke,
        refreshAkamaiToken,
        //buildProfileUrl,
        getReturnUrl,
        resetReturnUrl
    };
}